import { useCallback, useRef, useState } from 'react'
import { MapCameraChangedEvent } from '@vis.gl/react-google-maps'

import { DEFAULT_CAMERA } from '../config/googleMaps'

export type MapCamera = {
  zoom: number
  center: google.maps.LatLngLiteral
}

export type MapCameraRef = { current: MapCamera }

const STORAGE_KEY = 'crane:mapCamera'
const MAX_ZOOM = 22

const isCamera = (value: unknown): value is MapCamera => {
  if (!value || typeof value !== 'object') return false
  const { zoom, center } = value as Partial<MapCamera>
  return (
    typeof zoom === 'number' &&
    zoom >= 0 &&
    zoom <= MAX_ZOOM &&
    typeof center?.lat === 'number' &&
    Math.abs(center.lat) <= 90 &&
    typeof center?.lng === 'number' &&
    Math.abs(center.lng) <= 180
  )
}

const readCamera = (): MapCamera => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null')
    return isCamera(parsed)
      ? { zoom: parsed.zoom, center: { lat: parsed.center.lat, lng: parsed.center.lng } }
      : { ...DEFAULT_CAMERA }
  } catch {
    return { ...DEFAULT_CAMERA }
  }
}

const writeCamera = (camera: MapCamera) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(camera))
  } catch {
    /* localStorage niedostępny — kamera żyje tylko do odmontowania widoku. */
  }
}

export const useMapPosition = () => {
  const [initialCamera] = useState(readCamera)
  const cameraRef = useRef<MapCamera>(initialCamera)

  const handleCameraChanged = useCallback((event: MapCameraChangedEvent) => {
    const { zoom, center } = event.detail

    cameraRef.current = { zoom, center }
    writeCamera(cameraRef.current)
  }, [])

  return { cameraRef, handleCameraChanged }
}
